import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils";
import { Camera, ImageIcon, Mic, Upload } from "lucide-react";
import { useEffect, useRef, useState, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";

/**
 * The capture dock: a persistent bottom bar with the four capture actions —
 * Upload · Photos · Camera · Voice — sized for one-handed thumb reach. The
 * pickers are hidden `<input type="file">`s (WKWebView renders the native
 * picker/camera for free); what happens to the picked files is the parent's
 * call via `onUpload` — the scoped surface enqueues straight into its
 * workspace, the unscoped one opens the target sheet.
 *
 * Hides while the on-screen keyboard is likely up (an editable element is
 * focused): composing and capturing are different modes, and at phone height
 * the dock would just crowd the keyboard.
 */
export function MobileHomeActions({
  workspaceId,
  onUpload,
}: {
  /** Scoped workspace — forwarded to the voice page so chats land there. */
  workspaceId?: string;
  onUpload: (files: File[]) => void;
}) {
  const navigate = useNavigate();
  const keyboardUp = useKeyboardLikelyVisible();

  const filesInputRef = useRef<HTMLInputElement>(null);
  const photosInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const onPicked = (list: FileList | null, input: HTMLInputElement | null) => {
    const files = list ? Array.from(list) : [];
    // Same file re-picked later must fire change again.
    if (input) input.value = "";
    if (files.length) onUpload(files);
  };

  return (
    <div
      className={cn(
        "shrink-0 border-t bg-background px-2 pb-safe",
        keyboardUp && "hidden",
      )}
    >
      <div className="grid grid-cols-4 gap-1 py-1.5">
        <DockButton
          icon={Upload}
          label="Upload"
          onClick={() => filesInputRef.current?.click()}
        />
        <DockButton
          icon={ImageIcon}
          label="Photos"
          onClick={() => photosInputRef.current?.click()}
        />
        <DockButton
          icon={Camera}
          label="Camera"
          onClick={() => cameraInputRef.current?.click()}
        />
        <DockButton
          icon={Mic}
          label="Voice"
          accent
          onClick={() =>
            navigate(
              workspaceId
                ? `${ROUTES.MOBILE.VOICE}?workspaceId=${workspaceId}`
                : ROUTES.MOBILE.VOICE,
            )
          }
        />
      </div>

      {/* Hidden pickers — WKWebView renders the native picker/camera for free */}
      <input
        ref={filesInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => onPicked(e.target.files, e.target)}
      />
      <input
        ref={photosInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => onPicked(e.target.files, e.target)}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => onPicked(e.target.files, e.target)}
      />
    </div>
  );
}

function DockButton({
  icon: Icon,
  label,
  accent,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  /** The visually-primary action (Voice — the distinctly mobile capture). */
  accent?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl py-1.5 text-[11px] font-medium active:bg-accent",
        accent && "text-rust",
      )}
    >
      <Icon className="h-6 w-6" />
      {label}
    </button>
  );
}

/**
 * True while an editable element holds focus — the closest web-side signal
 * for "the on-screen keyboard is up" (`Keyboard.resize: "native"` resizes the
 * webview but exposes no DOM event without the Capacitor keyboard plugin).
 */
function useKeyboardLikelyVisible(): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const isEditable = (t: EventTarget | null): boolean =>
      t instanceof HTMLElement &&
      (t.isContentEditable ||
        t.tagName === "TEXTAREA" ||
        (t instanceof HTMLInputElement &&
          t.type !== "file" &&
          t.type !== "button" &&
          t.type !== "checkbox" &&
          t.type !== "radio"));
    const onFocusIn = (e: FocusEvent) => {
      if (isEditable(e.target)) setVisible(true);
    };
    const onFocusOut = (e: FocusEvent) => {
      if (isEditable(e.target)) setVisible(false);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  return visible;
}

export default MobileHomeActions;
